import { useMemo, useState } from 'react'
import Add from '@mui/icons-material/Add'
import { Button, Divider, ListItemText, Menu, MenuItem } from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import queryKeys from '@/service-library/query-keys'
import { DocumentRowValue } from '@/types/documents'
import AddListItemDialog from './fields/AddListItemDialog'
import { useProjectGridFieldsContext } from './ProjectGridFieldsProvider'
import useGetValueData from './useGetValueData'

type EntrataAddAliasButtonProps = {
  sourceFieldNames: string[]
  buildValues: (
    text: string,
    jsonValue: DocumentRowValue['final_value_json'],
  ) => Record<string, string>
  dataListId: string
  parentDataListId: string
  columnIdsByName: Record<string, string>
}

const aliasListQueryKeys = [
  queryKeys.dataListEntries.all,
  queryKeys.dataListEntryCellValues.all,
]

export default function EntrataAddAliasButton({
  sourceFieldNames,
  buildValues,
  dataListId,
  parentDataListId,
  columnIdsByName,
}: EntrataAddAliasButtonProps) {
  const menuOverlay = useOverlay()
  const dialogOverlay = useOverlay()
  const [prefillValues, setPrefillValues] = useState<Record<string, string>>()

  const fields = useProjectGridFieldsContext()
  const getValueData = useGetValueData()

  const sourceOptions = useMemo(
    () =>
      sourceFieldNames.flatMap((fieldName) => {
        const field = fields.find(({ name }) => name === fieldName)
        if (!field) return []
        const { value, jsonValue } = getValueData({ fieldId: field.id })
        const text = value?.trim()
        if (!text) return []
        const valuesByName = buildValues(text, jsonValue)
        return [{ fieldName, text, valuesByName }]
      }),
    [buildValues, fields, getValueData, sourceFieldNames],
  )

  function openDialog(valuesByName: Record<string, string>) {
    const valuesById = Object.entries(valuesByName).reduce<
      Record<string, string>
    >((acc, [columnName, value]) => {
      const columnId = columnIdsByName[columnName]
      if (columnId) acc[columnId] = value
      return acc
    }, {})
    setPrefillValues(valuesById)
    menuOverlay.close()
    dialogOverlay.open()
  }

  return (
    <>
      <Button
        variant="text"
        size="small"
        startIcon={<Add />}
        onClick={menuOverlay.open}
        sx={{ width: 'fit-content', fontSize: 12, ml: '-5px' }}
      >
        Add
      </Button>
      <Menu
        slotProps={{ list: { dense: true } }}
        anchorEl={menuOverlay.anchorEl as Element}
        open={menuOverlay.isOpen}
        onClose={menuOverlay.close}
      >
        {sourceOptions.map(({ fieldName, text, valuesByName }) => (
          <MenuItem key={fieldName} onClick={() => openDialog(valuesByName)}>
            <ListItemText
              primary={fieldName}
              secondary={text}
              slotProps={{
                primary: { variant: 'caption', color: 'text.secondary' },
                secondary: { variant: 'body2', color: 'text.primary' },
              }}
            />
          </MenuItem>
        ))}
        {!!sourceOptions.length && <Divider />}
        <MenuItem onClick={() => openDialog({})}>
          <ListItemText primary="Custom…" />
        </MenuItem>
      </Menu>
      <AddListItemDialog
        overlay={dialogOverlay}
        dataListId={dataListId}
        parentDataListId={parentDataListId}
        prefillValues={prefillValues}
        sideEffectQueryKeys={aliasListQueryKeys}
      />
    </>
  )
}
